import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import VideoPlayer from '@/components/VideoPlayer';
import { framedBy } from '@/lib/framedBy';

/**
 * The public, embeddable player — what an <iframe> on someone else's site loads.
 *
 * Deliberately spare in its dependencies:
 *
 * - No useAppApi/fetchApi, no useAuth, no useCan. This page renders inside a third-party
 *   frame where storage may be partitioned and there is no session to speak of; it needs a
 *   plain same-origin fetch and nothing else.
 * - No DefaultLayout. It is declared as a public route in App.jsx precisely so it gets no
 *   nav, no sidebar and no login redirect.
 * - `cache: 'no-store'`, and `credentials: 'include'` rather than 'same-origin': the watch
 *   grant is a cookie, and in a cross-site frame it only rides along on an include. (The
 *   cookie is SameSite=None; Secure for the same reason — see routes/video.js.)
 */
const GATE_INPUT = 'w-full rounded border border-white/20 bg-white/10 px-3 py-2 text-center text-white placeholder-white/40 focus:outline-none focus:border-white/50';

export default function Embed() {
  const { publicId } = useParams();
  const [playback, setPlayback] = useState(null);
  const [state, setState] = useState('loading');
  const [passcode, setPasscode] = useState('');
  const [viewerName, setViewerName] = useState('');
  const [viewerEmail, setViewerEmail] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [gateError, setGateError] = useState('');

  const fetchPlayback = useCallback(async () => {
    const res = await fetch(`/api/video/${encodeURIComponent(publicId)}/playback`, {
      credentials: 'include',
      cache: 'no-store',
    });
    if (res.ok) return res.json();

    let body = {};
    try { body = await res.json(); } catch { /* non-JSON error page */ }

    if (res.status === 401 && body.passcodeRequired) setState('passcode');
    else if (res.status === 401 || res.status === 403) setState('private');
    else if (res.status === 409) setState('processing');
    else if (res.status === 404) setState('missing');
    else setState('error');
    return null;
  }, [publicId]);

  useEffect(() => {
    let active = true;
    fetchPlayback()
      .then((data) => {
        if (!active || !data) return;
        setPlayback(data);
        setState('ready');
      })
      .catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, [fetchPlayback]);

  // Used by VideoPlayer to recover from a grant that expired mid-playback. Returning null
  // lets it fall back to its error state rather than swapping in a broken source.
  const refresh = useCallback(async () => {
    const next = await fetchPlayback().catch(() => null);
    if (next) setPlayback(next);
    return next;
  }, [fetchPlayback]);

  const unlock = async (e) => {
    e.preventDefault();
    setUnlocking(true);
    setGateError('');
    try {
      const res = await fetch(`/api/video/${encodeURIComponent(publicId)}/unlock`, {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode, viewerName, viewerEmail, framedBy: framedBy() }),
      });
      if (res.ok) {
        const next = await fetchPlayback();
        if (next) { setPlayback(next); setState('ready'); }
        return;
      }
      const body = await res.json().catch(() => ({}));
      setGateError(res.status === 429
        ? 'Too many attempts. Try again in a few minutes.'
        : (body.error || 'Incorrect passcode'));
    } catch {
      setGateError('Could not reach the server.');
    } finally {
      setUnlocking(false);
    }
  };

  const MESSAGES = {
    loading: 'Loading…',
    // A cross-site iframe gets no session cookie (the app's JWT cookie is SameSite=lax), so
    // this is what an owner sees too — hence "open it in the app" rather than "sign in".
    private: 'This video is private. Open it in the app to watch.',
    processing: 'This video is still being processed.',
    missing: 'This video is not available.',
    error: 'This video could not be loaded.',
  };

  if (state === 'passcode') {
    return (
      <div className="w-screen h-screen bg-black flex items-center justify-center p-4">
        <form onSubmit={unlock} className="w-full max-w-xs text-center space-y-3">
          <p className="text-white/80 text-sm">This video is protected.</p>
          <input
            type="text"
            value={viewerName}
            onChange={(e) => setViewerName(e.target.value)}
            placeholder="Your name"
            autoComplete="name"
            autoFocus
            className={GATE_INPUT}
          />
          <input
            type="email"
            value={viewerEmail}
            onChange={(e) => setViewerEmail(e.target.value)}
            placeholder="Your work email"
            autoComplete="email"
            className={GATE_INPUT}
          />
          <input
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Passcode"
            autoComplete="off"
            className={GATE_INPUT}
          />
          <button
            type="submit"
            disabled={unlocking || !passcode || !viewerName.trim() || !viewerEmail.trim()}
            className="w-full rounded bg-white/90 px-3 py-2 text-sm font-medium text-black disabled:opacity-50"
          >
            {unlocking ? 'Checking…' : 'Watch'}
          </button>
          {gateError ? <p className="text-red-300 text-xs">{gateError}</p> : null}
          {/* Says what happens, because the deterrent only works if the viewer knows about
              it — and because this collects personal data and ought to say so. */}
          <p className="text-white/45 text-[11px] leading-relaxed">
            Your name and email will be shown on the video while you watch, and recorded
            with your IP address and the time, so any copy can be traced.
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen bg-black flex items-center justify-center overflow-hidden">
      {state === 'ready' && playback
        ? <VideoPlayer playback={playback} onRefresh={refresh} autoPlay={false} />
        : <p className="text-white/70 text-sm px-4 text-center">{MESSAGES[state]}</p>}
    </div>
  );
}
